/* Fan info — compact layout (option 1b, rail + chips). The rail holds the two
   lists the fan profile feeds — TG Notifications and OF Comment — each with its
   own Enabled toggle and an "n/30" count. Fields are chips that wrap, one row
   per group, with a tri-state checkbox for the whole group. A list that's off
   fades its fields but keeps the picks. Fan analysis isn't shown: it's on
   whenever either list is on. Every change saves on its own, like Content and
   Monetisation — there is no Save button. Only the columns that changed are sent. */

import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';
import type { Account } from '../../../../api/client';
import { useAutosave } from '../../../../hooks/useAutosave';
import { Btn, Card, Icon, PageFrame, SaveStatus, SectionRail, Stack, Toggle, Warn, type RailRow } from '../kit';
import { font as f } from '../style';
import { C } from '../tokens';
import { ALL_FIELDS, FIELD_GROUPS, draftOf, normalise, payloadOf, storedOf, type Draft, type FieldGroup, type ListKey } from './model';

const LISTS: ListKey[] = ['notif', 'comments'];

export function FanInfoPage({ account, onAccountUpdate }: { account: Account; onAccountUpdate: (a: Account) => void }) {
  const { t } = useTranslation('assistantCompact');
  const { t: tc } = useTranslation('chatAssistant');
  const id = account.account_id;

  const [params, setParams] = useSearchParams();
  const sec: ListKey = params.get('s') === 'comments' ? 'comments' : 'notif';
  const select = (s: string) => { const n = new URLSearchParams(params); n.set('s', s); setParams(n, { replace: true }); };

  // The page is keyed by account, so the account it mounts with is the saved state.
  const [draft, setDraft] = useState<Draft>(() => draftOf(account));
  const saved = useRef(storedOf(account));
  const auto = useAutosave<Draft>({
    value: draft,
    delay: 400,
    save: async (d) => {
      const next = payloadOf(d) as Record<string, unknown>;
      const diff = Object.fromEntries(Object.entries(next).filter(([k, v]) => JSON.stringify(saved.current[k]) !== JSON.stringify(v)));
      if (!Object.keys(diff).length) return;
      const updated = await api.updateAccount(id, diff as Partial<Account>);
      saved.current = { ...saved.current, ...diff };
      onAccountUpdate(updated);
    },
  });
  // Switches save straight away; chip picks are debounced so quick clicks batch.
  const setNow = (fn: (d: Draft) => Draft) => { setDraft(fn); window.setTimeout(auto.flush, 0); };
  const setFields = (k: ListKey, keys: string[]) => setDraft((d) => ({ ...d, fields: { ...d.fields, [k]: normalise(keys) } }));

  const name = (k: ListKey) => t(`fi.s.${k}`);
  const total = ALL_FIELDS.length;
  const rail: RailRow[] = LISTS.map((k) => ({
    id: k,
    label: name(k),
    on: draft.on[k],
    onToggle: (v) => setNow((d) => ({ ...d, on: { ...d.on, [k]: v } })),
    meta: `${draft.fields[k].length}/${total}`,
    metaTitle: t('fi.countTip', { n: draft.fields[k].length, total }),
  }));

  const sel = draft.fields[sec];
  const on = draft.on[sec];
  const isC = sec === 'comments';
  const same = (a: string[], b: string[]) => a.join() === b.join();

  return (
    <PageFrame
      // Wider than the 196px default: each row carries a toggle and a count.
      rail={<SectionRail heading={t('fi.rail')} items={rail} active={sec} onSelect={select} width={240} />}
      headline={name(sec)}
      desc={t(`fi.d.${sec}`)}
      right={<SaveStatus status={auto.status} error={auto.error} onRetry={auto.retry} idleLabel />}
    >
      <Stack gap={12}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ font: f(500, 12), color: C.sub, whiteSpace: 'nowrap' }}>{t('fi.selected', { n: sel.length, total })}</span>
          <span style={{ flex: 1 }} />
          {isC && (
            <Btn kind="muted" icon="copy" title={t('fi.copyTip')} disabled={same(sel, draft.fields.notif)} onClick={() => setNow((d) => ({ ...d, fields: { ...d.fields, comments: [...d.fields.notif] } }))}>
              {t('fi.copy', { from: name('notif') })}
            </Btn>
          )}
          <Btn disabled={sel.length === total} onClick={() => setNow((d) => ({ ...d, fields: { ...d.fields, [sec]: [...ALL_FIELDS] } }))}>{t('fi.selectAll')}</Btn>
          <Btn disabled={sel.length === 0} onClick={() => setNow((d) => ({ ...d, fields: { ...d.fields, [sec]: [] } }))}>{t('fi.clear')}</Btn>
        </div>

        {isC && (
          <Card>
            <div className="cpt-row" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px' }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                <span style={{ font: f(600, 13), color: C.text, whiteSpace: 'nowrap' }}>{t('fi.overwrite')}</span>
                <span style={{ font: f(400, 12), color: C.muted }}>{t('fi.overwriteHelp')}</span>
              </div>
              <Toggle on={draft.overwrite} onChange={(v) => setNow((d) => ({ ...d, overwrite: v }))} />
            </div>
          </Card>
        )}

        {!on && <Warn>{t('fi.off', { name: name(sec) })}</Warn>}

        <div style={{ opacity: on ? 1 : 0.45, transition: 'opacity .15s' }}>
          {/* One grid for all groups (rows are subgrids) so the chips line up
              whatever the longest group name is in the current language. */}
          <Card style={{ display: 'grid', gridTemplateColumns: 'minmax(132px, max-content) minmax(0, 1fr)' }}>
            {FIELD_GROUPS.map((g) => (
              <GroupRow key={g.id} group={g} label={tc(`fanInfo.groups.${g.id}`)} sel={sel} fieldLabel={(l) => tc(`fanInfo.fields.${l}`)} tip={t('fi.groupTip')} onChange={(keys) => setFields(sec, keys)} />
            ))}
          </Card>
        </div>
      </Stack>
    </PageFrame>
  );
}

/** One group: a tri-state group checkbox (name + count) on the left, wrapping chips on the right. */
function GroupRow({ group, label, sel, fieldLabel, tip, onChange }: { group: FieldGroup; label: string; sel: string[]; fieldLabel: (l: string) => string; tip: string; onChange: (keys: string[]) => void }) {
  const keys = group.fields.map((x) => x.key);
  const n = keys.filter((k) => sel.includes(k)).length;
  const full = n === keys.length;
  const toggleGroup = () => onChange(full ? sel.filter((k) => !keys.includes(k)) : [...sel, ...keys]);
  const toggle = (k: string) => onChange(sel.includes(k) ? sel.filter((x) => x !== k) : [...sel, k]);
  return (
    <div className="cpt-fi-group" style={{ display: 'grid', gridColumn: '1 / -1', gridTemplateColumns: 'subgrid', columnGap: 14, padding: '12px 14px' }}>
      <button type="button" onClick={toggleGroup} title={tip} style={{ height: 28, display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ width: 14, height: 14, flex: 'none', boxSizing: 'border-box', borderRadius: 4, background: n ? C.purple : 'transparent', border: `1px solid ${n ? C.purple : 'var(--om-border-strong)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
          {n > 0 && <Icon name={full ? 'check' : 'minus'} size={10} />}
        </span>
        <span style={{ font: f(600, 11), letterSpacing: '.05em', textTransform: 'uppercase', color: C.purplePale, whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ font: f(500, 11), color: C.muted, whiteSpace: 'nowrap' }}>{n}/{keys.length}</span>
      </button>
      <div style={{ minWidth: 0, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {group.fields.map((x) => {
          const v = sel.includes(x.key);
          return (
            <button
              key={x.key}
              type="button"
              aria-pressed={v}
              onClick={() => toggle(x.key)}
              className={v ? undefined : 'cpt-hover-border'}
              style={{ height: 28, display: 'flex', alignItems: 'center', gap: 6, padding: '0 10px', borderRadius: 7, border: `1px solid ${v ? 'color-mix(in srgb, var(--om-accent) 55%, transparent)' : C.border}`, background: v ? C.tint16 : C.input, color: v ? C.text : C.sub, font: f(500, 12.5), cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              <Icon name={v ? 'check' : 'plus'} size={12} color={v ? C.purpleLt : C.muted} />
              {fieldLabel(x.label)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
